import {useEffect, useState} from "preact/compat";
import Cookies from "js-cookie";

// Special paths list
const PATHS = [
    "/entre",
    "/criar-conta",
    "/finalizar-cadastro",
    "/esqueci-a-senha",
    "/mudar-senha",
    "/checkout",
    "/nad",
    "/consent"
]

// Dedicated-component routes that are NOT home/CMS pages — banner is hidden here.
// Blacklist keeps the banner visible on all CMS product/category paths without
// needing to wait for async page-type data.
const NON_PROMO_BANNER_PATHS = [
    "/entre",
    "/criar-conta",
    "/finalizar-cadastro",
    "/esqueci-a-senha",
    "/mudar-senha",
    "/consent",
    "/busca",
    "/order",
    "/nad",
    "/editar-conta",
    "/enderecos",
    "/meus-pedidos",
    "/beneficiarios",
    "/senha",
    "/fale-conosco",
    "/termos-de-uso",
    "/perguntas-frequentes",
    "/politica-de-privacidade",
    "/mapa-do-site",
    "/not-found",
    "/error",
    "/onboarding",
]

/**
 *  Simple utility function to validate if something should behave differently
 *  if the user is located at one of the paths listed in the list located at the top of this file.
 *
 *  Data can be validated through the `isSpecialPath` flag returned from the object within the function
 *
 *  @returns {Object}
 * */
const useValidatePath = () => {
    // Stores the current path location.
    // It is being set at the app.js file on the page load.
    const [pathLocation, setPathLocation] = useState("");
    // Flag to validate if the current path is one of the special ones
    const [isSpecialPath, setIsSpecialPath] = useState(null);

    // Formats the current pathname without any parameters it may have
    const rawPathName = pathLocation.split("?")[0];

    // Checks if user is checking out
    const isCheckout = rawPathName.includes("/checkout");
    // Checks if user is at the nad feature page
    const isNad = rawPathName.includes("/nad");

    const shouldRenderHeader = !isCheckout && !isNad;

    const shouldRenderPromoBanner =
        shouldRenderHeader &&
        isSpecialPath === false &&
        !NON_PROMO_BANNER_PATHS.some(p => rawPathName === p || rawPathName.startsWith(p + "/"));

    // Removes all the cookies that are supposed to work only at the consent page
    // to prevent any unwanted side effects.
    const clearConsentCookies = () => {
        const COOKIES = [
            "must_show_preference_modal"
        ]

        COOKIES.forEach(cookie => {
            Cookies.remove(cookie);
        })
    }

    // Validates the page path on the page load
    useEffect(() => {
        // This used to be the initial implementation of this validation.
        // It'll be kept here just in case we need to roll back to it sometime.
        // setIsSpecialPath(
        //     PATHS.includes(pathLocation.split("?")[0])
        // );

        // Prevents component flicking at non-special-path pages
        if (!pathLocation) {
            return;
        }

        setIsSpecialPath(() => {
            let isIncluded = false;

            // This method was needed because if there were anything else other than path itself on the URL,
            // then the component wouldn't load as expected.
            PATHS.forEach(path => {
                /**
                 * We can't perform a shorthand attribution here (e.g.: isIncluded = pathLocation.split("?")[0].includes(path))
                 * because the value would be reset everytime the loop iterates through an item,
                 * and we don't want so as the current user path may actually be a special path while the last item of the array
                 * isn't. In a case like that, it would be set to false, even though it wasn't meant to be.
                 * So for now we shall keep it like this.
                 * */
                if (pathLocation.split("?")[0].includes(path)) {
                    isIncluded = true;
                }
            })

            return isIncluded;
        });

        if (!pathLocation.includes("/consent")) {
            clearConsentCookies();
        }
    }, [pathLocation]);

    return {
        pathLocation,
        setPathLocation,
        isSpecialPath,
        rawPathName,
        isCheckout,
        isNad,
        shouldRenderHeader,
        shouldRenderPromoBanner
    }
}

export default useValidatePath;